import * as Sentry from '@sentry/vue';
import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { DirectServiceLevelUpgrade, NextServiceLevel, ServiceLevelUpgradeMapping } from 'types/upgrade';
import { getServiceLevelUpgradeData, upgradeServiceLevelAndRiaPlan } from '@api/upgrade';
import { InvestmentStrategy, RiaPlan, RiaPlanWithId } from 'types/plan';
import { Location, RawLocation } from 'vue-router';
import { addFunds } from './add-funds';
import { app } from './app';
import { investmentEntity } from './investment-entity';
import { K1Acknowledgement } from 'types/form';
import { plans } from './plans';
import { redirect } from '@utils/client';
import { ServiceLevelName } from 'types/service-level';
import { setUnverifiedAccreditation } from '@api/accreditation';
import store from '..';
import { updateRiaPlan } from '@api/investment-entity';
import { UpgradeFlowAccreditationSelection } from 'types/accreditation';
import { user } from './user';

@Module({
	dynamic: true,
	namespaced: true,
	name: 'upgrade',
	store
})
class UpgradeModule extends VuexModule {
	nextLevelName: ServiceLevelName = 'NONE';
	nextLevelLabel = '';
	amountInvestedToDate = '';
	upgradeCommunicationThreshold = '';
	upgradeCommunicationEligible = false;
	aboveUpgradeThreshold = false;
	upgradeThreshold = '';
	amountNeededToUpgrade = '';
	hasAvailableNextLevel = false;
	acknowledgedK1Disclosure: K1Acknowledgement = { acknowledged: false };
	currentlySelectedRiaPlan: RiaPlan | undefined = undefined;
	newRiaPlan: RiaPlan | null = null;
	directUpgradeData: Array<DirectServiceLevelUpgrade> = [];
	upgradeLevelName: ServiceLevelName = 'NONE';
	upgradeLevelLabel = '';
	dataLoaded = false;

	get premiumShouldSeeAccreditationPage(): boolean {
		if (this.dataLoaded) {
			return investmentEntity.entityType === 'INDIVIDUAL' && !investmentEntity.isVerifiedAccredited;
		}
		return false;
	}

	get riaPlanInvestmentStrategy(): InvestmentStrategy | null {
		return this.newRiaPlan?.investmentStrategy || investmentEntity.currentRiaPlanInvestmentStrategy;
	}

	get currentRiaPlanInvestmentStrategy(): InvestmentStrategy | null {
		return investmentEntity.currentRiaPlanInvestmentStrategy;
	}

	get shouldSelectCorePlan(): boolean {
		return (
			investmentEntity.currentServiceLevel.name === 'STARTER' ||
			investmentEntity.currentServiceLevel.name === 'BASIC' ||
			investmentEntity.currentRiaPlanInvestmentStrategy === 'BASIC_PORTFOLIO'
		);
	}

	@Action({ rawError: true })
	public async fetchAndStoreCurrentlySelectedRiaPlan(): Promise<void> {
		const corePlans = await plans.getCorePlans();
		this.currentlySelectedRiaPlan = corePlans.find((plan) => plan.id === investmentEntity.currentSelectedRiaPlanId);
	}

	@Action({ rawError: true })
	public async fetchAndStoreServiceLevelUpgradeData(): Promise<void> {
		const slUpgradeData: ServiceLevelUpgradeMapping = await getServiceLevelUpgradeData();
		this.storeServiceLevelUpgradeData(slUpgradeData);
	}

	@Action({ rawError: true })
	public async storeServiceLevelUpgradeData(slUpgradeData: ServiceLevelUpgradeMapping): Promise<void> {
		const nextSlData: NextServiceLevel = slUpgradeData.nextServiceLevel;
		const directSlUpgradeData: Array<DirectServiceLevelUpgrade> = slUpgradeData.directServiceLevelUpgrades;
		this.UPDATE_NEXT_LEVEL_NAME(nextSlData?.name ?? '');
		this.UPDATE_NEXT_LEVEL_LABEL(nextSlData?.label ?? '');
		this.UPDATE_AMOUNT_INVESTED_TO_DATE(nextSlData?.amountInvestedToDate ?? '');
		this.UPDATE_UPGRADE_COMMUNICATION_THRESHOLD(nextSlData?.upgradeCommunicationThreshold ?? '');
		this.UPDATE_UPGRADE_COMMUNICATION_ELIGIBLE(nextSlData.upgradeCommunicationEligible);
		this.UPDATE_ABOVE_UPGRADE_THRESHOLD(nextSlData.aboveUpgradeThreshold);
		this.UPDATE_UPGRADE_THRESHOLD(nextSlData?.upgradeThreshold ?? '');
		this.UPDATE_AMOUNT_NEEDED_TO_UPGRADE(nextSlData?.amountNeededToUpgrade ?? '');
		this.UPDATE_HAS_AVAILABLE_NEXT_LEVEL(nextSlData.hasAvailableNextLevel);
		this.UPDATE_DIRECT_UPGRADE_DATA(directSlUpgradeData);
		this.updateUpgradeLevelData({ name: nextSlData?.name ?? '', label: nextSlData?.label ?? '' });
		this.UPDATE_DATA_LOADED(true);
	}

	@Action({ rawError: true })
	public async postUpgradeActions(): Promise<void> {
		await user.refreshUser();
		this.UPDATE_DATA_LOADED(false);
	}

	@Action({ rawError: true })
	public async startUpgrade(): Promise<string> {
		if (!this.aboveUpgradeThreshold && investmentEntity.entityType !== 'IRA') {
			addFunds.UPDATE_FORCE_INVEST_UPGRADE(true);
			return 'add-funds-ria-amount';
		} else if (!this.aboveUpgradeThreshold && investmentEntity.entityType === 'IRA') {
			redirect('/ira/add-funds');
		}

		if (investmentEntity.isIraEntity) {
			await upgradeServiceLevelAndRiaPlan({
				serviceLevel: this.nextLevelName,
				riaPlanId: investmentEntity.currentSelectedRiaPlanId
			});
			await this.postUpgradeActions();
			return 'upgrade-success';
		}

		switch (this.nextLevelName) {
			case 'BASIC':
				const basicPlanId = (await plans.getBasicPlan()).id;
				await upgradeServiceLevelAndRiaPlan({
					serviceLevel: 'BASIC',
					riaPlanId: basicPlanId
				});
				await this.postUpgradeActions();
				return 'upgrade-success';
			case 'CORE':
				return 'upgrade-core-plans';
			case 'ADVANCED':
				if (this.shouldSelectCorePlan) {
					return 'upgrade-core-plans';
				} else {
					await upgradeServiceLevelAndRiaPlan({
						serviceLevel: 'ADVANCED',
						riaPlanId: investmentEntity.currentSelectedRiaPlanId
					});
					await this.postUpgradeActions();
					return 'upgrade-success';
				}
			case 'PREMIUM':
				if (this.shouldSelectCorePlan) {
					return 'upgrade-core-plans';
				} else if (this.premiumShouldSeeAccreditationPage) {
					return 'upgrade-accreditation';
				} else {
					await upgradeServiceLevelAndRiaPlan({
						serviceLevel: 'PREMIUM',
						riaPlanId: investmentEntity.currentSelectedRiaPlanId
					});
					await this.postUpgradeActions();
					return 'upgrade-success';
				}
		}

		return 'account-overview';
	}

	@Action({ rawError: true })
	public async startInvestUpgrade(): Promise<string | undefined> {
		switch (this.upgradeLevelName) {
			case 'BASIC':
				const basicPlan = await plans.getBasicPlan();
				this.UPDATE_NEW_RIA_PLAN(basicPlan);
				return 'upgrade-view-allocation';
			case 'CORE':
				return 'upgrade-core-plans';
			case 'ADVANCED':
				if (this.shouldSelectCorePlan) {
					return 'upgrade-core-plans';
				}
				return (await this.upgradeToAdvancedOrPremium('ADVANCED')).name;
			case 'PREMIUM':
				if (this.shouldSelectCorePlan) {
					return 'upgrade-core-plans';
				} else if (investmentEntity.currentServiceLevel.name === 'CORE') {
					return (await this.upgradeToAdvancedOrPremium('PREMIUM')).name;
				}
				const addFundsAggreementsRoute = await addFunds.createRiaOrder({
					riaPlanId: investmentEntity.currentSelectedRiaPlanId,
					serviceLevelUpgradeName: 'PREMIUM'
				});
				if (investmentEntity.entityType === 'INDIVIDUAL' && !investmentEntity.isVerifiedAccredited) {
					return 'upgrade-accreditation';
				}
				return addFundsAggreementsRoute;
		}

		return 'account-overview';
	}

	@Action({ rawError: true })
	private async upgradeToAdvancedOrPremium(upgradeType: string): Promise<Location> {
		const riaPlan = (await plans.getAdvancedPlans()).find((plan) => {
			return plan.investmentStrategy === this.riaPlanInvestmentStrategy && plan.planType === 'STANDARD';
		});
		if (riaPlan) {
			try {
				return this.submitAdvancedPlanUpgrade(riaPlan) as Location;
			} catch (error) {
				Sentry.captureMessage(`Unable to submit Advanced Plan Upgrade: ${error}`);
			}
		} else {
			Sentry.captureMessage(`Upgrade ${upgradeType} failed to match a plan`);
		}
		return { name: 'account-overview' };
	}

	@Action({ rawError: true })
	public async submitPlanUpgradeKeepBasicPlan(basicPlan: RiaPlan): Promise<string> {
		this.UPDATE_NEW_RIA_PLAN(basicPlan);

		if (addFunds.investUpgrade) {
			if (this.upgradeLevelName === 'PREMIUM') {
				const agreementsRoute = await addFunds.createRiaOrder({
					riaPlanId: basicPlan.id,
					serviceLevelUpgradeName: this.upgradeLevelName
				});

				if (this.premiumShouldSeeAccreditationPage) {
					return 'upgrade-accreditation';
				} else {
					return agreementsRoute;
				}
			} else {
				return 'upgrade-view-allocation';
			}
		} else {
			if (this.nextLevelName === 'PREMIUM' && this.premiumShouldSeeAccreditationPage) {
				return 'upgrade-accreditation';
			} else {
				await upgradeServiceLevelAndRiaPlan({
					riaPlanId: basicPlan.id,
					serviceLevel: this.nextLevelName
				});
				await this.postUpgradeActions();

				return 'upgrade-success';
			}
		}
	}

	@Action({ rawError: true })
	public async submitCorePlanUpgrade(riaPlan: RiaPlanWithId): Promise<string | undefined> {
		const corePlan: RiaPlan = {
			id: riaPlan.id,
			name: riaPlan.name,
			investmentStrategy: riaPlan.investmentStrategy,
			planType: 'STANDARD'
		};
		this.UPDATE_NEW_RIA_PLAN(corePlan);

		if (addFunds.investUpgrade) {
			if (this.upgradeLevelName === 'ADVANCED' || this.upgradeLevelName === 'PREMIUM') {
				return (await this.upgradeToAdvancedOrPremium(this.upgradeLevelName)).name;
			}
			return 'upgrade-view-allocation';
		} else {
			if (this.nextLevelName === 'PREMIUM' && this.premiumShouldSeeAccreditationPage) {
				return 'upgrade-accreditation';
			} else {
				await upgradeServiceLevelAndRiaPlan({
					serviceLevel: this.nextLevelName,
					riaPlanId: riaPlan.id
				});
				await this.postUpgradeActions();
				return 'upgrade-success';
			}
		}
	}

	@Action({ rawError: true })
	public async submitAdvancedPlanUpgrade(riaPlan: RiaPlan): Promise<RawLocation> {
		if (addFunds.investUpgrade || addFunds.investTabAddFunds) {
			this.UPDATE_NEW_RIA_PLAN(riaPlan);
			if (this.upgradeLevelName === 'PREMIUM') {
				const addFundsAggreementsRoute = await addFunds.createRiaOrder({
					riaPlanId: this.newRiaPlan?.id || '',
					serviceLevelUpgradeName: 'PREMIUM'
				});
				if (investmentEntity.entityType === 'INDIVIDUAL' && !investmentEntity.isVerifiedAccredited) {
					return { name: 'upgrade-accreditation' };
				}
				return { name: addFundsAggreementsRoute };
			} else {
				return { name: 'upgrade-view-allocation' };
			}
		} else {
			const selectingPlusPlanNotUpgrading =
				investmentEntity.currentServiceLevel.name === 'ADVANCED' ||
				investmentEntity.currentServiceLevel.name === 'PREMIUM';
			if (selectingPlusPlanNotUpgrading) {
				await updateRiaPlan(riaPlan.id);
				await this.postUpgradeActions();
				app.ADD_TOAST({
					type: 'success',
					message: 'Success! Your preference has been saved.'
				});
				return {
					name: 'settings-investment-plan-view'
				};
			} else {
				await upgradeServiceLevelAndRiaPlan({
					serviceLevel: 'ADVANCED',
					riaPlanId: riaPlan.id
				});
				await this.postUpgradeActions();
				return { name: 'upgrade-success' };
			}
		}
	}

	@Action({ rawError: true })
	public async submitPremiumPlanUpgrade(accreditationSelection: UpgradeFlowAccreditationSelection): Promise<string> {
		if (addFunds.investUpgrade) {
			await setUnverifiedAccreditation(accreditationSelection);
			return 'add-funds-ria-agreements';
		} else {
			const riaPlanId = this.newRiaPlan?.id ?? investmentEntity.currentSelectedRiaPlanId;

			await setUnverifiedAccreditation(accreditationSelection);
			await upgradeServiceLevelAndRiaPlan({
				serviceLevel: 'PREMIUM',
				riaPlanId
			});
			await this.postUpgradeActions();
			return 'upgrade-success';
		}
	}

	@Action({ rawError: true })
	public updateUpgradeLevelData(data: { name: ServiceLevelName; label: string }): void {
		this.UPDATE_UPGRADE_LEVEL_NAME(data.name);
		this.UPDATE_UPGRADE_LEVEL_LABEL(data.label);
	}

	@Mutation
	public UPDATE_NEXT_LEVEL_NAME(name: ServiceLevelName): void {
		this.nextLevelName = name;
	}

	@Mutation
	public UPDATE_NEXT_LEVEL_LABEL(label: string): void {
		this.nextLevelLabel = label;
	}

	@Mutation
	public UPDATE_AMOUNT_INVESTED_TO_DATE(amountInvestedToDate: string): void {
		this.amountInvestedToDate = amountInvestedToDate;
	}

	@Mutation
	public UPDATE_UPGRADE_COMMUNICATION_THRESHOLD(upgradeCommunicationThreshold: string): void {
		this.upgradeCommunicationThreshold = upgradeCommunicationThreshold;
	}

	@Mutation
	public UPDATE_UPGRADE_COMMUNICATION_ELIGIBLE(upgradeCommunicationEligible: boolean): void {
		this.upgradeCommunicationEligible = upgradeCommunicationEligible;
	}

	@Mutation
	public UPDATE_ABOVE_UPGRADE_THRESHOLD(aboveUpgradeThreshold: boolean): void {
		this.aboveUpgradeThreshold = aboveUpgradeThreshold;
	}

	@Mutation
	public UPDATE_UPGRADE_THRESHOLD(upgradeThreshold: string): void {
		this.upgradeThreshold = upgradeThreshold;
	}

	@Mutation
	public UPDATE_AMOUNT_NEEDED_TO_UPGRADE(amountNeededToUpgrade: string): void {
		this.amountNeededToUpgrade = amountNeededToUpgrade;
	}

	@Mutation
	public UPDATE_HAS_AVAILABLE_NEXT_LEVEL(hasAvailableNextLevel: boolean): void {
		this.hasAvailableNextLevel = hasAvailableNextLevel;
	}

	@Mutation
	public UPDATE_ACKNOWLEDGED_K1_DISCLOSURE(acknowledgedK1Disclosure: K1Acknowledgement): void {
		this.acknowledgedK1Disclosure = acknowledgedK1Disclosure;
	}

	@Mutation
	public UPDATE_DATA_LOADED(loaded: boolean): void {
		this.dataLoaded = loaded;
	}

	@Mutation
	public UPDATE_NEW_RIA_PLAN(newPlan: RiaPlan | null): void {
		this.newRiaPlan = newPlan;
	}

	@Mutation
	public UPDATE_DIRECT_UPGRADE_DATA(directUpgradeData: Array<DirectServiceLevelUpgrade>): void {
		this.directUpgradeData = directUpgradeData;
	}

	@Mutation
	public UPDATE_UPGRADE_LEVEL_NAME(upgradeLevelName: ServiceLevelName): void {
		this.upgradeLevelName = upgradeLevelName;
	}

	@Mutation
	public UPDATE_UPGRADE_LEVEL_LABEL(upgradeLevelLabel: string): void {
		this.upgradeLevelLabel = upgradeLevelLabel;
	}
}

export const upgrade = getModule(UpgradeModule);
